//
// CommonJS because server.js runs untranspiled, so security-headers.js can only
// require() plain CJS. Webpack handles the interop for the client-side importers.
//
// Every string here is hashed into script-src, so none may contain config:
// security-headers.js hashes at runtime from node-config while the page emits
// from the bundle, where DefinePlugin inlined config at build time. Config
// travels in a type="application/json" block, which CSP ignores as a data
// block, or in a data- attribute on an external script's tag.
//
const WAF_COOKIE_DOMAINS_ID = 'scite-waf-cookie-domains'
const DEFERRED_STYLESHEET_ID = 'scite-font-stylesheet'
const STORE_STATE_ID = 'scite-store-state'
const THEME_STORAGE_KEY = 'scite:theme'
const DARK_SCHEME = '(prefers-color-scheme: dark)'

//
// SCI-3961 names every inline script with a `sourceURL` directive so its stack
// frames carry a name instead of the document URL. That name is part of what
// script-src hashes, so it has to be applied here, where the text is defined,
// rather than at emission time — appending it to an already-hashed body is what
// would break the match. `nameInlineScripts` leaves an already-named script
// alone, so the two passes compose; inline-script-hashes.test.js pins the
// directive built here against the one client/lib/inline-script.ts builds.
//
const named = (name, code) => `${code}\n//# sourceURL=/inline/${name}.js`

const wafCookieDomainBootstrap = named(
  'aws-waf-config',
  `(function(){var el=document.getElementById('${WAF_COOKIE_DOMAINS_ID}');if(!el)return;try{window.awsWafCookieDomainList=JSON.parse(el.textContent)}catch(e){}})();`
)

// Applies the stored theme before first paint so a dark page doesn't flash light while
// the bundle loads. applyThemeOptIn validates the name and handles ?theme= afterwards.
const themeBootstrap = named(
  'theme',
  `(function(){try{var t=localStorage.getItem('${THEME_STORAGE_KEY}');if(t==='system')t=matchMedia('${DARK_SCHEME}').matches?'dark':'light';if(t&&t!=='light')document.documentElement.dataset.theme=t}catch(e){}})();`
)

// The el.sheet branch matters on a warm cache, where the stylesheet can finish
// before the parser reaches this script and the load event is already gone.
const deferredStylesheetActivator = named(
  'font-stylesheet',
  `(function(){var el=document.getElementById('${DEFERRED_STYLESHEET_ID}');if(!el)return;var show=function(){el.media='all'};if(el.sheet){show()}else{el.addEventListener('load',show,{once:true})}})();`
)

// Config only. The agent itself used to be pasted in beneath this — 64 KB of
// nr-loader-spa-1.303.0.min.js, re-sent uncached on every page load and frozen
// at whatever version was current when someone copied it. It now loads from
// js-agent.newrelic.com, which script-src already allowed.
const newRelicConfig = named(
  'newrelic',
  [
    ';window.NREUM||(NREUM={});NREUM.init={distributed_tracing:{enabled:true},privacy:{cookies_enabled:true},ajax:{deny_list:["bam.nr-data.net"]}};',
    ';NREUM.loader_config={accountID:"6897873",trustKey:"6897873",agentID:"1120425501",licenseKey:"NRJS-f8f3d50d190191f9db3",applicationID:"1120425501"};',
    ';NREUM.info={beacon:"bam.nr-data.net",errorBeacon:"bam.nr-data.net",licenseKey:"NRJS-f8f3d50d190191f9db3",applicationID:"1120425501",sa:1};'
  ].join('')
)

//
// Injected at runtime by client/lib/rewardful.js once cookie consent allows it,
// rather than emitted by the server like everything else here. It is hashed the
// same way regardless: script-src governs an inline script by its bytes, not by
// what put it on the page. rewardful.js imports this string so both sides stay
// on identical bytes.
//
const rewardfulBootstrap = named(
  'rewardful',
  `
        (function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');
    `
)

//
// Vendor loader stubs, verbatim from vendor-injection.js apart from indentation.
// Each stub only fetches the vendor's current script, so holding the stub here
// pins nothing — openai-ads and churnkey are absent because they interpolate
// config and are external scripts instead.
//
const gtmStub = named(
  'gtm',
  `
window.dataLayer = window.dataLayer || [];
function gtag(){ dataLayer.push(arguments); }
gtag("consent", "default", {
  ad_storage: "denied",
  analytics_storage: "denied",
  wait_for_update: 1500
});
gtag("js", new Date());
gtag("config", "AW-614125746");
`
)

const bingStub = named(
  'bing',
  `
(function(w,d,t,r,u)
{
  var f,n,i;
  w[u]=w[u]||[],f=function()
  {
    var o={ti:"343050743", enableAutoSpaTracking: true};
    o.q=w[u];
    if(typeof UET !== "undefined"){w[u]=new UET(o),w[u].push("pageLoad")}
  },
  n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function()
  {
    var s=this.readyState;
    s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)
  },
  i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)
})
(window,document,"script","//bat.bing.com/bat.js","uetq");
`
)

const facebookPixelStub = named(
  'facebook-pixel',
  `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '927411631440706');
fbq('track', 'PageView');
`
)

const mathJaxConfig = named(
  'mathjax',
  `
window.__MathJax_State__ = {
    isReady: false,
    promise: new Promise(resolve => {
    window.MathJax = {
        // MathJax can be configured as desired in addition to these options.
        startup: {
        ready: () => {
            // Do whatever MathJax would normally do at this point.
            MathJax.startup.defaultReady();
            // Set the flag and resolve the promise.
            window.__MathJax_State__.isReady = true;
            resolve();
        }
        },
        tex: {
        inlineMath: [
            ["$", "$"],
        ],
        },
        svg: {
            fontCache: "global"
        }
    };
    })
};
`
)

module.exports = {
  WAF_COOKIE_DOMAINS_ID,
  DEFERRED_STYLESHEET_ID,
  STORE_STATE_ID,
  THEME_STORAGE_KEY,
  DARK_SCHEME,
  themeBootstrap,
  wafCookieDomainBootstrap,
  deferredStylesheetActivator,
  newRelicConfig,
  rewardfulBootstrap,
  gtmStub,
  bingStub,
  facebookPixelStub,
  mathJaxConfig,
  inlineScriptTexts: [
    themeBootstrap,
    wafCookieDomainBootstrap,
    deferredStylesheetActivator,
    newRelicConfig,
    rewardfulBootstrap,
    gtmStub,
    bingStub,
    facebookPixelStub,
    mathJaxConfig
  ]
}
